/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { PermissionService, UtilService } from '@dm/core';
import { runningStatusE } from 'app/common/constants';
import { LicenseKey } from 'app/common/constants/license-key';
import { KeyValuePipe } from 'app/common/pipes/key-value.pipe';
import { type FileSystem } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { ProductConfig, SWITCH } from 'app/config/switch';
import { FilesystemSnapshotStrService } from 'app/feature/protection/object/filesystem/filesystem-snapshot/filesystem-snapshot-str.service';
import { CloneStrService } from 'app/feature/protection/object/lun/clone/clone-str.service';
import { HyperMetroPairConst } from 'app/feature/protection/object/lun/hyper-metro/hyper-metro-pair-str-service';
import { RepPairStrService } from 'app/feature/protection/object/lun/rep-pair/rep-pair-str.service';
import { ReplicationStrService } from 'app/feature/protection/object/protect-group-entry/replication/replication-str.service';
import { CDPStrService } from 'app/feature/protection/schedule/hyper-cdp/cdp/cdp-str.service';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type DataConfig, type Tip, DataTypeE, DataURL, GlobalSearchStrService } from '../../global-search-str.service';
import { GlobalSearchConfigService } from '../global-search-config.service';

@Injectable({ providedIn: 'root' })
export class ConfigProtectionStrService {
    protectionDataMap: { [key: string]: DataConfig };

    FeatureConfig = FeatureConfig;

    constructor(
        private configService: GlobalSearchConfigService,
        private str: GlobalSearchStrService,
        private keyValuePipe: KeyValuePipe,
        private utilService: UtilService,
        private cloneStrService: CloneStrService,
        private lunRepStrService: RepPairStrService,
        private lunHyperStrServcie: HyperMetroPairConst,
        private fsSnapshotStr: FilesystemSnapshotStrService,
        private fsRepStrService: ReplicationStrService,
        private blockCdpStrService: CDPStrService,
        private permissionService: PermissionService,
    ) { }

    isHideSearchProtectionFileSystem(): boolean {
        const fsHyperSnapR = this.permissionService.hasPermission('fs_snapshot_R') && this.permissionService.isLicenseExist(['HyperCDP']);
        const fsHyperCloneR = this.permissionService.isLicenseExist(['HyperClone']);
        const fsHyperReplicationR = this.permissionService.hasPermission('remote_replication_R') && this.permissionService.isLicenseExist(['HyperReplication']);
        const fsHyperMetroR = this.permissionService.hasPermission('hyper_metro_pair_R');
        return !fsHyperSnapR && !fsHyperReplicationR && !fsHyperCloneR && !fsHyperMetroR;
    }

    /**
     * 返回dataMap
     */
    getDataMap(): { [key: string]: DataConfig } {
        this.protectionDataMap = {
            /**
             * 保护租户
             */
            ...(SWITCH.SUPPORT_NAS && !SWITCH.IS_VSTORE_SYSTEM ?
                {
                    /**
                     * 保护租户
                     */
                    ...(FeatureConfig.IsShowProtectVstore ? {
                        [DataTypeE.protectVstore]: {
                            type: DataTypeE.protectVstore,
                            url: '/protection/v-store',
                            tab: this.str.i18n.protectVstoreTab,
                            page: {
                                searchKey: 'home.search_term_PGvStore_lab',
                                permission: ['hyper_metro_pair_R', '@vstore_R'],
                            },
                        },
                    } : {}),
                    /**
                     * 远程复制租户pair
                     */
                    ...(FeatureConfig.IsSupportRemoteRepPairVstore ? {
                        [DataTypeE.remoteRepPairVstore]: {
                            type: DataTypeE.remoteRepPairVstore,
                            url: '/protection/v-store',
                            tab: this.str.i18n.remoteRepPairVstoreTab,
                            license: [LicenseKey.HyperReplication],
                            operation: [
                                {
                                    searchKey: 'protection.vstore_task_create_remoteRep_title',
                                    action: 'searchCreate',
                                    permission: ['hyper_metro_pair_R', 'task_W', 'port_W', 'hyper_metro_pair_W', 'vstore_W', 'remote_device_R'],
                                },
                            ],
                            page: {
                                searchKey: 'protection.vstore_task_view_remoteRepPairs_tab',
                                permission: ['hyper_metro_pair_R', '@vstore_R'],
                            },
                        },
                    } : {}),
                    /**
                     * 双活租户pair
                     */
                    ...(!ProductConfig.isSupportVstoreHyperMetroPair ? {} : {
                        [DataTypeE.hypermetroPairVstore]: {
                            type: DataTypeE.hypermetroPairVstore,
                            url: '/protection/v-store',
                            tab: this.str.i18n.hypermetroPairVstoreTab,
                            page: {
                                searchKey: 'protection.vstore_task_view_hypermetroPairs_tab',
                                permission: ['hyper_metro_pair_R', '@vstore_R'],
                            },
                        },
                    }),
                } :
                {}
            ),

            /**
             * 保护组
             */
            [DataTypeE.protectGroup]: {
                type: DataTypeE.protectGroup,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/protect-group' : '/protection/protection-group',
                tab: this.str.i18n.protectionGroupTab,
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                license: [LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'protection.protectionGroup_task_create_title',
                        action: 'searchCreate',
                        permission: ['protect_group_R', 'protect_group_W'],
                    },
                ],
                page: {
                    searchKey: 'protection.protectionGroup_title',
                    permission: ['protect_group_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.dataProtectionTitle,
                },
            },
            /**
             * 快照一致性组
             */
            [DataTypeE.snapshotGroup]: {
                type: DataTypeE.snapshotGroup,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/protect-group' : '/protection/snapshot',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.snapshotCGTab : this.str.i18n.protectionGroupTab,
                license: [[LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP], [LicenseKey.SmartThin]],
                operation: [
                    {
                        searchKey: 'protection.snapshotCG_term_create_title',
                        action: 'searchCreate',
                        permission: ['protect_group_R', 'snap_group_R', 'snap_group_W'],
                    },
                ],
                page: {
                    searchKey: 'protection.snapshotCGs_title',
                    permission: ['protect_group_R', 'snap_group_R'],
                },
            },
            /**
             *  克隆一致性组
             */
            [DataTypeE.cloneGroup]: {
                type: DataTypeE.cloneGroup,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/protect-group' : '/protection/clone',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.cloneCGTab : this.str.i18n.protectionGroupTab,
                license: [LicenseKey.HyperClone, LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'protection.cloneCG_task_createCloneCG_title',
                        action: 'searchCreate',
                        permission: ['protect_group_R', 'hyper_clone_group_R', 'hyper_clone_group_W'],
                    },
                ],
                page: {
                    searchKey: 'protection.cloneCG_title',
                    permission: ['protect_group_R', 'hyper_clone_group_R'],
                },
            },
            /**
             * 远程复制一致性组
             */
            [DataTypeE.repGroup]: {
                type: DataTypeE.repGroup,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/protect-group' : '/protection/replication',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.replicationCGTab : this.str.i18n.protectionGroupTab,
                license: [LicenseKey.HyperReplication, LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'protection.replicationCG_task_create_title',
                        action: 'searchCreate',
                        permission: ['protect_group_R', 'consistency_group_R', 'remote_replication_W'],
                    },
                ],
                page: {
                    searchKey: 'protection.replicationCG_title',
                    permission: ['protect_group_R', 'consistency_group_R'],
                },
            },
            /**
             * 双活一致性组
             */
            [DataTypeE.hyperMetroGroup]: {
                type: DataTypeE.hyperMetroGroup,
                url: '/protection/object/protect-group',
                tab: this.str.i18n.hyperMetroCGroupTab,
                license: [LicenseKey.HyperMetro, LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'protection.hyperMetroCG_task_create_title',
                        action: 'searchCreate',
                        permission: ['protect_group_R', 'hyper_metro_consistency_group_R', 'hyper_metro_consistency_group_W'],
                    },
                ],
                page: {
                    searchKey: 'protection.hyperMetroCGs_title',
                    permission: ['protect_group_R', 'hyper_metro_consistency_group_R'],
                },
            },
            /**
             *  保护组环形3DC
             */
            ...(!FeatureConfig.IsSupportDrStar ? {} : {
                [DataTypeE.drStarGroup]: {
                    type: DataTypeE.drStarGroup,
                    url: '/protection/object/protect-group',
                    tab: this.str.i18n.drStarPgTab,
                    license: [LicenseKey.HyperReplication, LicenseKey.SmartThin],
                    operation: [
                        {
                            searchKey: 'home.search_term_create3DCbyPG_lab',
                            action: 'searchCreate',
                            actionParams: 'pg',
                            permission: ['protect_group_R', 'dr_star_R', 'dr_star_W'],
                        },
                        {
                            searchKey: 'home.search_term_create3DCbyCG_lab',
                            action: 'searchCreate',
                            actionParams: 'cg',
                            permission: ['protect_group_R', 'dr_star_R', 'dr_star_W'],
                        },
                    ],
                    page: {
                        searchKey: 'home.search_term_3DCbyPG_lab',
                        permission: ['protect_group_R', 'dr_star_R'],
                    },
                },
            }),

            /**
             * 保护组-LUN
             */
            ...FeatureConfig.IsShowMenuItemsForOceanProtect ? {} : {
                [DataTypeE.protectLun]: {
                    type: DataTypeE.protectLun,
                    url: '/protection/object/lun',
                    tab: this.str.i18n.protectLunTab,
                    license: [LicenseKey.SmartThin],
                    isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                    page: {
                        searchKey: 'home.search_term_PGLUN_lab',
                        permission: ['lun_R'],
                    },
                },
            },
            /**
             * 保护组-LUN快照
             */
            [DataTypeE.lunSnapshot]: {
                type: DataTypeE.lunSnapshot,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/lun' : '/protection/snapshot',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.lunSnapshotTab : this.str.i18n.protectLunTab,
                license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                operation: [
                    {
                        searchKey: 'home.search_term_createLUNsnap_lab',
                        action: 'searchCreate',
                        permission: ['lun_R', 'lun_snapshot_R', 'lun_snapshot_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_LUNSnapshots_lab',
                    permission: ['lun_R', 'lun_snapshot_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.lunsnapshot, { timeConversion: 0 }).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.keyValuePipe.transform(data.RUNNINGSTATUS, runningStatusE),
                                },
                                {
                                    label: this.str.i18n.sourceLunLab,
                                    value: data.SOURCELUNNAME,
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 保护-LUN克隆
             */
            [DataTypeE.clone]: {
                type: DataTypeE.clone,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/lun' : '/protection/clone',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.cloneTab : this.str.i18n.protectLunTab,
                license: [LicenseKey.SmartThin, LicenseKey.HyperClone],
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                operation: [
                    {
                        searchKey: 'home.search_term_createLUNClonePair_lab',
                        action: 'searchCreate',
                        permission: ['lun_R', 'hyper_clone_R', 'hyper_clone_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_LUNClones_lab',
                    permission: ['lun_R', 'hyper_clone_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.clone).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.pairHealthStatusLab,
                                    status: parseInt(data.copyStatus, 10) + 1,
                                    value: this.str.statusMap[parseInt(data.copyStatus, 10) + 1].label,
                                },
                                {
                                    label: this.str.i18n.pairRunStatusLab,
                                    value: this.utilService.getLabelFromOptions(this.cloneStrService.syncStatusOptions, data.syncStatus),
                                },
                                {
                                    label: this.str.i18n.sourceLunLab,
                                    value: data.sourceName,
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 保护-LUN远程复制Pair
             */
            [DataTypeE.lunRep]: {
                type: DataTypeE.lunRep,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/lun' : '/protection/replication',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.lunRepTab : this.str.i18n.protectLunTab,
                license: [LicenseKey.SmartThin, LicenseKey.HyperReplication],
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                operation: [
                    {
                        searchKey: 'home.search_term_createLUNRemotePair_lab',
                        action: 'searchCreate',
                        permission: ['lun_R', 'remote_replication_R', 'remote_replication_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_LUNremotePair_lab',
                    permission: ['lun_R', 'remote_replication_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.replicationpair).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.lunRepStrService.runningStatusObj[data.RUNNINGSTATUS],
                                },
                                {
                                    label: this.str.i18n.repModLab,
                                    value: this.lunRepStrService.replicationModeObj[data.REPLICATIONMODEL],
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 保护-LUN双活Pair
             */
            [DataTypeE.lunHyperMetro]: {
                type: DataTypeE.lunHyperMetro,
                url: '/protection/object/lun',
                tab: this.str.i18n.lunHyperMetroTab,
                license: [LicenseKey.SmartThin, LicenseKey.HyperMetro],
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                operation: [
                    {
                        searchKey: 'home.search_term_createLUNHyperPair_lab',
                        action: 'searchCreate',
                        permission: ['lun_R', 'hyper_metro_pair_R', 'hyper_metro_pair_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_LUNHyperPair_lab',
                    permission: ['lun_R', 'hyper_metro_pair_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.hypermetropair).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.lunHyperStrServcie.runningStatusObj[data.RUNNINGSTATUS],
                                },
                                {
                                    label: this.str.i18n.sourceLunLab,
                                    value: data.LOCALOBJNAME,
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             *  保护-LUN环形3DC
             */
            ...(!FeatureConfig.IsSupportDrStar ? {} : {
                [DataTypeE.drStar]: {
                    type: DataTypeE.drStar,
                    url: '/protection/object/lun',
                    tab: this.str.i18n.drStarTab,
                    license: [LicenseKey.HyperReplication, LicenseKey.SmartThin],
                    operation: [
                        {
                            searchKey: 'home.search_term_create3DCbyLUN_lab',
                            action: 'searchCreate',
                            actionParams: 'pg',
                            permission: ['lun_R', 'dr_star_R', 'dr_star_W'],
                        },
                        {
                            searchKey: 'home.search_term_create3DCbyPair_lab',
                            action: 'searchCreate',
                            actionParams: 'cg',
                            permission: ['lun_R', 'dr_star_R', 'dr_star_W'],
                        },
                    ],
                    page: {
                        searchKey: 'home.search_term_LUN3DC_lab',
                        permission: ['lun_R', 'dr_star_R'],
                    },
                },
            }),

            /**
             * 保护文件系统
             */
            ...FeatureConfig.IsShowMenuItemsForOceanProtect ? {} : {
                [DataTypeE.protectFs]: {
                    type: DataTypeE.protectFs,
                    url: '/protection/object/filesystem',
                    tab: this.str.i18n.protectFsTab,
                    isNASFeature: true,
                    page: {
                        searchKey: 'home.search_term_PGFS_lab',
                        permission: ['file_system_R'],
                    },
                    hide: this.isHideSearchProtectionFileSystem(),
                },
            },
            /**
             * 保护文件系统-快照
             * 因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.fsSnapshot]: {
                type: DataTypeE.fsSnapshot,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/filesystem' : '/protection/snapshot',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.fsSnapshotTab : this.str.i18n.protectFsTab,
                license: [[LicenseKey.HyperSnap, LicenseKey.HyperCDP]],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.quickstart_task_createFSsnap_btn',
                        action: 'searchCreate',
                        permission: ['fs_snapshot_R', 'fs_snapshot_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_FSsnap_lab',
                    permission: ['fs_snapshot_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.fssnapshot, { ID: id }).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.snapTypeLab,
                                    value: this.fsSnapshotStr.snapTypeRender(data.snapType),
                                },
                                {
                                    label: this.str.i18n.createTimeLab,
                                    value: this.fsSnapshotStr.timeRender(data.utcTimeStamp),
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 保护文件系统-克隆
             */
            [DataTypeE.fsClone]: {
                type: DataTypeE.fsClone,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/filesystem' : '/protection/clone',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.fsCloneTab : this.str.i18n.protectFsTab,
                license: [LicenseKey.HyperClone],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'protection.fs_hyperClone_task_create_title',
                        action: 'createAction',
                        permission: ['file_system_R', 'file_system_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_FSClone_lab',
                    permission: ['file_system_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.fs).pipe(
                        map(({ data }: { data: FileSystem }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.usageLab,
                                    value: this.utilService.adaptUnit(data.allocatedPoolQuota, 'Sector').label,
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.keyValuePipe.transform(data.RUNNINGSTATUS, runningStatusE),
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 保护文件系统-远程复制Pair
             */
            [DataTypeE.fsRep]: {
                type: DataTypeE.fsRep,
                url: !FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/object/filesystem' : '/protection/replication',
                tab: !FeatureConfig.IsShowMenuItemsForOceanProtect ? this.str.i18n.fsRepTab : this.str.i18n.protectFsTab,
                license: [LicenseKey.HyperReplication],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.quickstart_task_createFSreplication_btn',
                        action: 'searchCreate',
                        permission: ['file_system_R', 'remote_replication_R', 'remote_replication_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_FSremotePair_lab',
                    permission: ['file_system_R', 'remote_replication_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.replicationpair).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.fsRepStrService.runningStatusObj[data.RUNNINGSTATUS],
                                },
                                {
                                    label: this.str.i18n.repModLab,
                                    value: this.fsRepStrService.replicationModeObj[data.REPLICATIONMODEL],
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 保护文件系统-双活Pair
             */
            ...!FeatureConfig.IsShowProtectFSHyperMetro ? {} : {
                [DataTypeE.fsHyperMetro]: {
                    type: DataTypeE.fsHyperMetro,
                    url: '/protection/object/filesystem',
                    tab: this.str.i18n.fsHyperMetro,
                    isNASFeature: true,
                    isFsHyperMetroFeature: true,
                    operation: [
                        {
                            searchKey: 'home.search_term_createFSHyperPair_lab',
                            action: 'searchCreate',
                            permission: ['file_system_R', 'hyper_metro_pair_W'],
                        },
                    ],
                    page: {
                        searchKey: 'home.search_term_FSHyperPair_lab',
                        permission: ['file_system_R', 'hyper_metro_pair_R'],
                    },
                },
            },

            /**
             * 块定时HyperCDP计划
             */
            [DataTypeE.timeBlockCdp]: {
                type: DataTypeE.timeBlockCdp,
                url: '/protection/plan/hyper-cdp',
                license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                tab: this.str.i18n.blockTimeCdpTab,
                operation: [
                    {
                        searchKey: 'home.search_term_createBlockCDPschedule_lab',
                        action: 'searchCreate',
                        permission: [['lun_R', 'protect_group_R'], ['@hyper_cdp_W']],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_blockCDPschedule_lab',
                    permission: [['lun_R', 'protect_group_R'], ['@hyper_cdp_R']],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.dataProtectionTitle,
                },
            },
            /**
             * 块HyperCDP一致性组
             */
            [DataTypeE.blockCdpGroup]: {
                type: DataTypeE.blockCdpGroup,
                url: '/protection/plan/hyper-cdp',
                tab: this.str.i18n.blockCdpGroupTab,
                license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'home.search_term_createBlockCDPCG_lab',
                        action: 'searchCreate',
                        permission: ['protect_group_R', '@hyper_cdp_W'],
                        license: [LicenseKey.HyperCDP, LicenseKey.HyperSnap, LicenseKey.SmartThin],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_blockCDPCG_lab',
                    permission: ['protect_group_R', '@hyper_cdp_R'],
                },
            },
            /**
             * 块HyperCDP
             * 因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.blockCdp]: {
                type: DataTypeE.blockCdp,
                url: '/protection/plan/hyper-cdp',
                tab: this.str.i18n.blockCdpTab,
                license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'home.search_term_createBlockCDP_lab',
                        action: 'searchCreate',
                        permission: ['lun_R', '@hyper_cdp_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_blockCDP_lab',
                    permission: ['lun_R', '@hyper_cdp_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.hypercdp).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.blockCdpStrService.runningStatusRender(data.RUNNINGSTATUS),
                                },
                                {
                                    label: this.str.i18n.activeTimeLab,
                                    value: this.blockCdpStrService.timeRender(data.TIMESTAMP),
                                },
                                {
                                    label: this.str.i18n.scheduleLab,
                                    value: this.blockCdpStrService.yesNoRender(data.ISSCHEDULEDCDP),
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 文件定时HyperCDP计划
             */
            [DataTypeE.timeFsCdp]: {
                type: DataTypeE.timeFsCdp,
                url: '/protection/plan/file-hyper-cdp',
                tab: this.str.i18n.fsTimeCdpTab,
                license: [LicenseKey.HyperCDP],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.search_term_createFSCDPschedule_lab',
                        action: 'searchCreate',
                        permission: ['file_system_R', '@hyper_cdp_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_FSCDPschedule_lab',
                    permission: ['file_system_R', '@hyper_cdp_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.dataProtectionTitle,
                },
            },
            /**
             * 文件HyperCDP
             * 因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.fsCdp]: {
                type: DataTypeE.fsCdp,
                url: '/protection/plan/file-hyper-cdp',
                tab: this.str.i18n.fsCdpTab,
                license: [LicenseKey.HyperCDP],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.search_term_createFSCDP_lab',
                        action: 'searchCreate',
                        permission: ['file_system_R', 'fs_snapshot_W', '@hyper_cdp_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_FSCDP_lab',
                    permission: ['file_system_R', 'fs_snapshot_R', '@hyper_cdp_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.hypercdp).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.createTimeLab,
                                    value: this.fsSnapshotStr.timeRender(data.utcTimeStamp),
                                },
                                {
                                    label: this.str.i18n.scheduleLab,
                                    value: this.fsSnapshotStr.yesNoRender(data.isScheduleCdp),
                                },
                            ];
                            return result;
                        }),
                    ),
            },

            /**
             * 远端设备
             */
            [DataTypeE.remoteDevice]: {
                type: DataTypeE.remoteDevice,
                url: '/protection/config/remote-device',
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                operation: [
                    {
                        searchKey: 'protection.configuration_remoteDevice_task_addRemoteDevice_title',
                        action: 'createRemoteDevice',
                        permission: ['remote_device_R', 'remote_device_W'],
                    },
                ],
                page: {
                    searchKey: 'common.remoteDevices_title',
                    permission: ['remote_device_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.dataProtectionTitle,
                },
            },

            /**
             * 双活域
             */
            ...(!FeatureConfig.IsSupportHyperMetro ? {} : {
                [DataTypeE.hyperMetroDomain]: {
                    type: DataTypeE.hyperMetroDomain,
                    url: '/protection/config/hyper-metro-domain',
                    tab: this.str.i18n.hyperMetroDomainTab,
                    isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideProtection,
                    operation: [
                        {
                            searchKey: 'home.search_term_createBlockHyperDomain_lab',
                            action: 'searchCreate',
                            actionParams: 'block',
                            license: [LicenseKey.HyperMetro, LicenseKey.SmartThin],
                            permission: ['hyper_metro_domain_R', 'hyper_metro_domain_W', 'remote_device_R'],
                        },
                        ...(!FeatureConfig.IsSupportNASHyperMetro ? [] : [
                            {
                                searchKey: 'home.search_term_createFSHyperDomain_lab',
                                action: 'searchCreate',
                                actionParams: 'fs',
                                permission: ['hyper_metro_domain_R', 'hyper_metro_domain_W', 'remote_device_R'],
                                notSupportFsHyperMetroDomainFeature: !SWITCH.SUPPORT_NAS,
                            },
                        ]),
                    ],
                    page: {
                        searchKey: 'common.HyperMetroDomains_title',
                        permission: ['hyper_metro_domain_R'],
                        license: [[LicenseKey.NAS, LicenseKey.HyperMetro]],
                    },
                },
            }),
        };

        return this.protectionDataMap;
    }
}
